import React, { useEffect, useRef, useState } from 'react';
import MessageActions from './MessageActions';
import EditMessage from './EditMessage';
import DeleteConfirmModal from './DeleteConfirmModal';
import MessageReactions from './MessageReactions';
import MessageWithLinkPreview from './MessageWithLinkPreview';
import FilePreview from './FilePreview';
import './MessageList.css';

function MessageList({ messages, users, loading, onThreadClick, onEditMessage, onDeleteMessage, currentUserId, onAddReaction, onRemoveReaction, onBookmark, onRemind, channelId, lastReadTimestamp, channelError }) {
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, message: null });
  const [, setCurrentTime] = useState(Date.now());
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevChannelIdRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);
  const hasScrolledOnLoadRef = useRef(false);
  const lastMessageTsRef = useRef(null);

  // Force re-render every minute to update "Due in X minutes" text
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const scrollToBottom = (instant = false) => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    } else if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: instant ? 'instant' : 'smooth' });
    }
  };

  // Reset scroll flag when channel changes
  useEffect(() => {
    if (channelId !== prevChannelIdRef.current) {
      prevChannelIdRef.current = channelId;
      hasScrolledOnLoadRef.current = false;
      prevMessagesLengthRef.current = 0;
    }
  }, [channelId]);

  // Scroll to bottom when messages first load or channel changes
  useEffect(() => {
    if (!loading && messages.length > 0 && !hasScrolledOnLoadRef.current) {
      hasScrolledOnLoadRef.current = true;
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom(true);
        });
      });
    }
  }, [loading, messages]);

  // Scroll when new messages arrive (after initial load)
  useEffect(() => {
    if (!hasScrolledOnLoadRef.current || messages.length === 0) {
      prevMessagesLengthRef.current = messages.length;
      lastMessageTsRef.current = messages.length > 0 ? messages[messages.length - 1]?.ts : null;
      return;
    }

    const currentLastTs = messages.length > 0 ? messages[messages.length - 1]?.ts : null;
    const hasNewMessage = messages.length > prevMessagesLengthRef.current ||
                          (currentLastTs && currentLastTs !== lastMessageTsRef.current);

    if (hasNewMessage && prevMessagesLengthRef.current > 0) {
      // Use requestAnimationFrame to ensure DOM is updated with new message
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });
    }

    prevMessagesLengthRef.current = messages.length;
    lastMessageTsRef.current = currentLastTs;
  }, [messages]);

  const formatTime = (timestamp) => {
    const date = new Date(parseFloat(timestamp) * 1000);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatLastReplyTime = (timestamp) => {
    const date = new Date(parseFloat(timestamp) * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // Today - show time
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit'
      });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      // Within a week - show day name
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    } else {
      // Older - show date
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const handleEdit = (message) => {
    setEditingMessageId(message.ts);
  };

  const handleSaveEdit = (message, newText) => {
    onEditMessage(message, newText);
    setEditingMessageId(null);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
  };

  const handleDelete = (message) => {
    setDeleteModal({ isOpen: true, message });
  };

  const confirmDelete = () => {
    if (deleteModal.message) {
      onDeleteMessage(deleteModal.message);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, message: null });
  };

  const handleReply = (message) => {
    onThreadClick(message);
  };

  const getDateLabel = (timestamp) => {
    const messageDate = new Date(parseFloat(timestamp) * 1000);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Reset time parts for comparison
    const messageDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    if (messageDateOnly.getTime() === todayOnly.getTime()) {
      return 'Today';
    } else if (messageDateOnly.getTime() === yesterdayOnly.getTime()) {
      return 'Yesterday';
    } else {
      // Format as "Monday, January 15"
      return messageDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const shouldShowDateDivider = (currentMessage, previousMessage) => {
    if (!previousMessage) return true; // Always show for first message

    const currentDate = new Date(parseFloat(currentMessage.ts) * 1000);
    const previousDate = new Date(parseFloat(previousMessage.ts) * 1000);

    // Check if dates are different
    return (
      currentDate.getDate() !== previousDate.getDate() ||
      currentDate.getMonth() !== previousDate.getMonth() ||
      currentDate.getFullYear() !== previousDate.getFullYear()
    );
  };

  // Find the unread marker position using lastReadTimestamp from Slack API
  const unreadMarkerIndex = lastReadTimestamp
    ? messages.findIndex(msg => parseFloat(msg.ts) > parseFloat(lastReadTimestamp))
    : -1;

  // Log unread marker position for debugging
  useEffect(() => {
    if (lastReadTimestamp && unreadMarkerIndex >= 0) {
      console.log('📬 Unread marker at index:', unreadMarkerIndex, 'lastRead:', lastReadTimestamp);
    }
  }, [lastReadTimestamp, unreadMarkerIndex]);

  if (loading) {
    return (
      <div className="message-list">
        <div className="messages-loading">
          <div className="loading-spinner"></div>
          <span>Loading messages...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="message-list" ref={messagesContainerRef}>
      <div className="messages-container">
        {channelError && channelError.type === 'bot_not_in_channel' && (
          <div className="channel-error-banner">
            <div className="channel-error-icon">⚠️</div>
            <div className="channel-error-content">
              <div className="channel-error-message">{channelError.message}</div>
              <div className="channel-error-instruction">
                <code>{channelError.instruction}</code>
              </div>
            </div>
          </div>
        )}
        {messages.map((message, index) => {
          const showDateDivider = shouldShowDateDivider(message, messages[index - 1]);
          const showUnreadMarker = index === unreadMarkerIndex;
          const user = users[message.user] || { real_name: 'Unknown User' };
          return (
            <React.Fragment key={message.ts || index}>
              {showDateDivider && (
                <div className="date-divider">
                  <div className="date-divider-line"></div>
                  <div className="date-divider-label">{getDateLabel(message.ts)}</div>
                  <div className="date-divider-line"></div>
                </div>
              )}

              {showUnreadMarker && (
                <div className="unread-marker">
                  <div className="unread-marker-line"></div>
                  <div className="unread-marker-label">New</div>
                  <div className="unread-marker-line"></div>
                </div>
              )}

              <div className={`message ${message.is_starred ? 'message-bookmarked' : ''}`}>
              <div className="message-avatar">
                {user.profile?.image_48 ? (
                  <img src={user.profile.image_48} alt={user.real_name} />
                ) : (
                  <div className="avatar-placeholder">
                    {user.real_name?.charAt(0) || '?'}
                  </div>
                )}
              </div>
              <div className="message-content">
                <div className="message-header">
                  <span className="message-author">{user.real_name || user.name}</span>
                  <span className="message-time">{formatTime(message.ts)}</span>
                  {message.edited && <span className="message-edited">(edited)</span>}
                </div>
                {editingMessageId === message.ts ? (
                  <EditMessage
                    message={message}
                    onSave={handleSaveEdit}
                    onCancel={handleCancelEdit}
                  />
                ) : (
                  <MessageWithLinkPreview message={message} users={users} />
                )}
                {message.files && message.files.length > 0 && (
                  <div className="message-files">
                    {message.files.map((file, fileIndex) => (
                      <FilePreview key={file.id || fileIndex} file={file} />
                    ))}
                  </div>
                )}
                {message.reactions && message.reactions.length > 0 && (
                  <MessageReactions
                    reactions={message.reactions}
                    messageId={message.ts}
                    onAddReaction={(messageId, emoji) => onAddReaction(message, emoji)}
                    onRemoveReaction={(messageId, emoji) => onRemoveReaction(message, emoji)}
                    currentUserId={currentUserId}
                    users={users}
                  />
                )}
                {message.reply_count > 0 && (
                  <div 
                    className="thread-indicator" 
                    onClick={() => onThreadClick && onThreadClick(message)}
                  >
                    <span className="thread-count">{message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}</span>
                    {message.latest_reply && (
                      <>
                        <span className="thread-last-reply">Last reply {formatLastReplyTime(message.latest_reply)}</span>
                        <span className="thread-view-hover">View thread</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              {message.is_starred && (
                <div className="message-bookmark-indicator" title="Saved for later">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 2h10v12l-5-3-5 3V2z" fill="#1d1c1d"/>
                  </svg>
                  <span>Saved for later</span>
                  {message.reminder_time && (() => {
                    const now = Math.floor(Date.now() / 1000);
                    const timeLeft = message.reminder_time - now;
                    if (timeLeft > 0) {
                      const minutes = Math.floor(timeLeft / 60);
                      const hours = Math.floor(minutes / 60);
                      const days = Math.floor(hours / 24);

                      let timeText;
                      if (days > 0) {
                        timeText = `Due in ${days} day${days > 1 ? 's' : ''}`;
                      } else if (hours > 0) {
                        timeText = `Due in ${hours} hour${hours > 1 ? 's' : ''}`;
                      } else if (minutes > 0) {
                        timeText = `Due in ${minutes} min${minutes > 1 ? 's' : ''}`;
                      } else {
                        timeText = 'Due soon';
                      }

                      return (
                        <>
                          <span className="reminder-separator">•</span>
                          <span className="reminder-due-time">{timeText}</span>
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              <MessageActions
                message={message}
                currentUserId={currentUserId}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onReply={handleReply}
                onAddReaction={onAddReaction}
                onBookmark={onBookmark}
                onRemind={onRemind}
              />
              </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message={deleteModal.message}
        user={deleteModal.message ? users[deleteModal.message.user] : null}
      />
    </div>
  );
}

export default MessageList;